import { type ReactNode, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/pro-solid-svg-icons/faSearch";
import {
    LayoutGrid,
    Search as SearchIcon,
    type LucideIcon,
} from "lucide-react";
import { cn } from "~/lib/utils";
import { getCatalogIconStyles } from "~/modules/catalog/lib/getCatalogIconStyles";
import { FilterMetadata, FilterType, FilterTypes } from "../types";

// // // //

// Display buckets for the category sidebar + row badges. Purely derived from
// FilterMetadata.type — no buckets are hard-coded against specific fields.
type TypeBucket =
    | "search"
    | "text"
    | "select"
    | "dollar"
    | "number"
    | "percent"
    | "date"
    | "boolean";

const TYPE_BUCKETS: Record<FilterType, TypeBucket> = {
    [FilterTypes.String]: "text",
    [FilterTypes.Search]: "search",
    [FilterTypes.StringSingleSelect]: "select",
    [FilterTypes.Dollar]: "dollar",
    [FilterTypes.Float]: "number",
    [FilterTypes.PositiveFloat]: "number",
    [FilterTypes.NegativeFloat]: "number",
    [FilterTypes.Integer]: "number",
    [FilterTypes.PositiveInteger]: "number",
    [FilterTypes.NegativeInteger]: "number",
    [FilterTypes.Percent]: "percent",
    [FilterTypes.Date]: "date",
    [FilterTypes.RelativeDate]: "date",
    [FilterTypes.RelativePastDate]: "date",
    [FilterTypes.RelativeFutureDate]: "date",
    [FilterTypes.Boolean]: "boolean",
};

const BUCKET_META: Record<
    TypeBucket,
    {
        typeLabel: string;
        sidebarLabel: string;
        // A bucket renders either a Lucide icon or a glyph on its row badge
        glyph: string | null;
        icon?: LucideIcon;
        badgeClassName: string;
    }
> = {
    search: {
        typeLabel: "Search",
        sidebarLabel: "Search",
        glyph: null,
        icon: SearchIcon,
        badgeClassName: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
    },
    text: {
        typeLabel: "Text",
        sidebarLabel: "Text",
        glyph: "Aa",
        badgeClassName: "bg-muted text-secondary-foreground",
    },
    select: {
        typeLabel: "Select",
        sidebarLabel: "Select",
        glyph: "≡",
        badgeClassName: "bg-primary/10 text-primary",
    },
    dollar: {
        typeLabel: "Amount",
        sidebarLabel: "Amounts",
        glyph: "$",
        badgeClassName:
            "bg-dollar-green/10 text-dollar-green dark:bg-emerald-500/15 dark:text-emerald-400",
    },
    number: {
        typeLabel: "Count",
        sidebarLabel: "Counts",
        glyph: "#",
        badgeClassName: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
    },
    percent: {
        typeLabel: "Percent",
        sidebarLabel: "Percentages",
        glyph: "%",
        badgeClassName: "bg-teal-500/10 text-teal-600 dark:text-teal-400",
    },
    date: {
        typeLabel: "Date",
        sidebarLabel: "Dates",
        glyph: "◷",
        badgeClassName: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
    },
    boolean: {
        typeLabel: "Boolean",
        sidebarLabel: "Yes / No",
        glyph: "◐",
        badgeClassName: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    },
};

// Sidebar bucket order. Buckets absent from the data are dropped. "search" is
// intentionally omitted - the Search field is pinned above the grouped list
// instead of appearing as its own sidebar category.
const BUCKET_ORDER: TypeBucket[] = [
    "select",
    "text",
    "dollar",
    "number",
    "percent",
    "date",
    "boolean",
];

// // // //

// A field's category grouping key: its catalog `category` label when the field
// carries category metadata, otherwise the bucket derived from its type (the
// legacy behavior).
function categoryKeyFor(metadata: FilterMetadata): string {
    return metadata.category?.label ?? TYPE_BUCKETS[metadata.type];
}

// The Search field is never grouped with the rest - it's pinned above the
// list and has no sidebar category of its own.
function isSearchField(metadata: FilterMetadata): boolean {
    return metadata.type === FilterTypes.Search;
}

// Sidebar group headings, indexed by the catalogRank a SidebarCategory carries:
// 0 = metric, 1 = reference_data, 2 = legacy type buckets.
const SIDEBAR_GROUP_LABELS = ["Metrics", "Reference Data", "Other"];

// The icon + color swatch for a field's category, rendered both on the field
// row and on that category's sidebar entry. Catalog fields get their category's
// Lucide icon tinted with the catalog text + background color; legacy
// (uncategorized) fields fall back to the type-bucket glyph badge.
type CategoryBadge = {
    icon: LucideIcon | null;
    glyph: string | null;
    className: string;
};

// The neutral swatch for the "All fields" entry, which has no category
const ALL_FIELDS_BADGE: CategoryBadge = {
    icon: LayoutGrid,
    glyph: null,
    className: "bg-muted text-muted-foreground",
};

function categoryBadgeFor(metadata: FilterMetadata): CategoryBadge {
    const { category } = metadata;

    if (category) {
        const { Icon, iconClassName, bgClassName } = getCatalogIconStyles({
            icon: category.icon,
            color: category.color,
        });

        return {
            icon: Icon,
            glyph: null,
            className: cn(bgClassName, iconClassName),
        };
    }

    const bucket = BUCKET_META[TYPE_BUCKETS[metadata.type]];
    return {
        icon: bucket.icon ?? null,
        glyph: bucket.glyph,
        className: bucket.badgeClassName,
    };
}

// A resolved sidebar entry for one category, from either catalog metadata or a
// legacy type bucket.
type SidebarCategory = {
    key: string;
    label: string;
    badge: CategoryBadge;
    catalogRank: number; // primary sidebar sort (metric < reference_data < legacy)
    order: number; // secondary sidebar sort
};

function sidebarCategoryFor(metadata: FilterMetadata): SidebarCategory {
    const { category } = metadata;
    const badge = categoryBadgeFor(metadata);

    if (category) {
        return {
            key: category.label,
            label: category.label,
            badge,
            catalogRank: category.catalog_type === "metric" ? 0 : 1,
            order: category.order,
        };
    }

    const bucket = TYPE_BUCKETS[metadata.type];
    return {
        key: bucket,
        label: BUCKET_META[bucket].sidebarLabel,
        badge,
        catalogRank: 2,
        order: BUCKET_ORDER.indexOf(bucket),
    };
}

// // // //

// Returns true if every character of `match` appears in `str` in order
function isSubsequence(str: string, match: string): boolean {
    let i = 0;
    for (let n = 0; n < str.length && i < match.length; n++) {
        if (str[n] === match[i]) i++;
    }
    return i === match.length;
}

// Fuzzy score across label + description + documentation; higher ranks first
function scoreMatch(metadata: FilterMetadata, match: string): number {
    const label = metadata.content.label.toLowerCase();
    const description = (metadata.content.description || "").toLowerCase();
    const documentation = (metadata.content.documentation || "").toLowerCase();

    if (label.startsWith(match)) return 100;
    if (label.includes(match)) return 60;
    if (isSubsequence(label, match)) return 30;
    if (description.includes(match)) return 15;
    if (documentation.includes(match)) return 10;
    return 0;
}

// Fields matching `match`, best score first, ties broken alphabetically
function searchMetadata(
    metadata: FilterMetadata[],
    match: string,
): FilterMetadata[] {
    return metadata
        .map((each) => ({ each, score: scoreMatch(each, match) }))
        .filter((scored) => scored.score > 0)
        .sort(
            (a, b) =>
                b.score - a.score ||
                a.each.content.label.localeCompare(b.each.content.label),
        )
        .map((scored) => scored.each);
}

// // // //

export function FieldSelectPanel(props: {
    value: string;
    filterMetadata: FilterMetadata[];
    compact: boolean;
    onChange: (updatedValue: string) => void;
}) {
    const { filterMetadata, onChange, compact } = props;

    const [lookupString, setLookupString] = useState("");
    const [category, setCategory] = useState<"all" | string>("all");
    const [rawActiveIndex, setRawActiveIndex] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);

    // Get list of items matching the selected category + user lookup
    let lookupResults: FilterMetadata[] = [...filterMetadata];
    if (category !== "all") {
        lookupResults = lookupResults.filter(
            (each) => categoryKeyFor(each) === category,
        );
    }
    const match = lookupString.toLowerCase().trim();
    if (match) {
        lookupResults = searchMetadata(lookupResults, match);
    } else {
        // No active search: pin the Search field above everything else (only
        // meaningful while browsing "All fields", since it's no longer a
        // selectable sidebar category), then group every other field by
        // category so same-category fields are always contiguous (stable
        // within each group, so the source order is otherwise kept).
        const pinned =
            category === "all" ? lookupResults.filter(isSearchField) : [];
        const rest = lookupResults
            .filter((each) => !isSearchField(each))
            .sort((a, b) => {
                const catA = sidebarCategoryFor(a);
                const catB = sidebarCategoryFor(b);
                return (
                    catA.catalogRank - catB.catalogRank ||
                    catA.order - catB.order
                );
            });
        lookupResults = [...pinned, ...rest];
    }

    const activeIndex = Math.max(
        0,
        Math.min(rawActiveIndex, lookupResults.length - 1),
    );

    // Keep the keyboard-active row visible while navigating
    useEffect(() => {
        const activeRow = listRef.current?.querySelector(
            '[data-active="true"]',
        );
        activeRow?.scrollIntoView({ block: "nearest" });
    }, [activeIndex]);

    // Sidebar categories, derived from the unfiltered metadata. Categories
    // absent from the data are dropped.
    const categoryByKey = new Map<string, SidebarCategory>();
    filterMetadata.forEach((each) => {
        if (isSearchField(each)) return;
        const cat = sidebarCategoryFor(each);
        if (!categoryByKey.has(cat.key)) {
            categoryByKey.set(cat.key, cat);
        }
    });

    // Categories grouped under their catalog_type heading, each group ordered
    // by CategoryMetadata.order. Empty groups are dropped.
    const sidebarGroups = SIDEBAR_GROUP_LABELS.map((label, catalogRank) => ({
        label,
        categories: [...categoryByKey.values()]
            .filter((each) => each.catalogRank === catalogRank)
            .sort((a, b) => a.order - b.order),
    })).filter((group) => group.categories.length > 0);

    function categoryCount(key: "all" | string): number {
        if (key === "all") return filterMetadata.length;
        return filterMetadata.filter((each) => categoryKeyFor(each) === key)
            .length;
    }

    function selectField(selected: FilterMetadata) {
        onChange(selected.field);
        setLookupString("");
        setRawActiveIndex(0);
    }

    // A search that came back empty. Without a search the list can only be
    // empty when there's no metadata at all, since every category has results.
    const isEmptySearch = lookupResults.length === 0 && match !== "";

    // How many fields the same search matches outside the selected category -
    // when there are some, the category is what's hiding them
    const matchesOutsideCategory =
        isEmptySearch && category !== "all"
            ? searchMetadata(filterMetadata, match).length
            : 0;

    const activeCategoryLabel = categoryByKey.get(category)?.label;

    // One sidebar entry, shared by "All fields" and the grouped categories
    function renderCategoryButton(cat: {
        key: "all" | string;
        label: string;
        badge: CategoryBadge;
    }) {
        const isActive = category === cat.key;
        const BadgeIcon = cat.badge.icon;
        return (
            <button
                key={cat.key}
                type="button"
                onClick={() => {
                    setCategory(cat.key);
                    setRawActiveIndex(0);
                }}
                className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
                    isActive
                        ? "bg-primary/10 font-semibold text-primary"
                        : "font-medium text-secondary-foreground hover:bg-muted/60",
                )}
            >
                <span
                    className={cn(
                        "flex h-5 w-5 flex-none items-center justify-center rounded font-mono text-[10px] font-semibold",
                        cat.badge.className,
                    )}
                >
                    {BadgeIcon ? (
                        <BadgeIcon className="h-3 w-3" />
                    ) : (
                        cat.badge.glyph
                    )}
                </span>
                <span className="flex-1 truncate text-left">{cat.label}</span>
                <span
                    className={cn(
                        "text-xs tabular-nums",
                        isActive ? "text-primary" : "text-muted-foreground",
                    )}
                >
                    {categoryCount(cat.key)}
                </span>
            </button>
        );
    }

    // One row in the results pane
    function renderFieldRow(each: FilterMetadata, index: number) {
        const badge = categoryBadgeFor(each);
        const BadgeIcon = badge.icon;
        const isActive = index === activeIndex;
        return (
            <button
                key={each.field}
                type="button"
                data-active={isActive ? "true" : "false"}
                onClick={() => selectField(each)}
                onMouseEnter={() => setRawActiveIndex(index)}
                className={cn(
                    "flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left",
                    isActive && "bg-accent",
                )}
            >
                <span
                    className={cn(
                        "mt-px flex h-7 w-7 flex-none items-center justify-center rounded-md font-mono text-sm font-semibold",
                        badge.className,
                    )}
                >
                    {BadgeIcon ? (
                        <BadgeIcon className="h-4 w-4" />
                    ) : (
                        badge.glyph
                    )}
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-sm font-semibold leading-tight text-foreground">
                        {each.content.label}
                    </span>
                    {each.content.description && (
                        <span className="text-xs leading-snug text-tertiary-foreground">
                            {each.content.description}
                        </span>
                    )}
                </span>
            </button>
        );
    }

    // Field rows for the results pane. While browsing "All fields" without an
    // active search, a category header precedes the first row of each new
    // group so the grouping (sorted above) is visible, not just implied by
    // sort order. The Search field is pinned above every group and never
    // gets a header of its own.
    const showCategoryHeaders = category === "all" && !match;
    const fieldRows: ReactNode[] = [];
    let previousGroupKey: string | null = null;
    lookupResults.forEach((each, index) => {
        if (showCategoryHeaders && !isSearchField(each)) {
            const cat = sidebarCategoryFor(each);
            if (cat.key !== previousGroupKey) {
                fieldRows.push(
                    <div
                        key={`header-${cat.key}`}
                        className="px-2.5 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                    >
                        {cat.label}
                    </div>,
                );
            }
            previousGroupKey = cat.key;
        }
        fieldRows.push(renderFieldRow(each, index));
    });

    return (
        <div
            className={cn(
                "flex max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl bg-popover text-popover-foreground shadow-2xl",
                compact ? "w-180" : "w-[906px]",
            )}
        >
            <div className="flex items-center gap-2.5 border-b px-3.5 py-3">
                <FontAwesomeIcon
                    icon={faSearch}
                    className="h-4 w-4 flex-none text-muted-foreground"
                />
                <input
                    autoFocus
                    type="text"
                    value={lookupString}
                    placeholder="Search fields…"
                    autoComplete="off"
                    className="min-w-0 flex-1 border-0 bg-transparent p-0 text-base font-medium text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0"
                    onChange={(e) => {
                        setLookupString(e.target.value);
                        // Searching spans every field - keeping a category
                        // selected would silently hide matches from the results
                        setCategory("all");
                        setRawActiveIndex(0);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setRawActiveIndex(
                                Math.min(
                                    activeIndex + 1,
                                    lookupResults.length - 1,
                                ),
                            );
                        } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setRawActiveIndex(Math.max(activeIndex - 1, 0));
                        } else if (e.key === "Enter") {
                            e.preventDefault();
                            const selected = lookupResults[activeIndex];
                            if (selected) selectField(selected);
                        } else if (e.key === "Escape" && lookupString) {
                            // First Escape clears the search; the next one
                            // bubbles up to dismiss the enclosing popover
                            e.preventDefault();
                            e.stopPropagation();
                            setLookupString("");
                            setRawActiveIndex(0);
                        }
                    }}
                />
                <kbd className="flex-none rounded border px-1.5 py-0.5 font-sans text-[11px] tracking-wide text-muted-foreground">
                    ↑↓ ⏎
                </kbd>
            </div>

            <div className="flex items-stretch">
                {/* NOTE - the sidebar's max-h and the field list's h below
                    must stay in sync so the two columns end at the same line */}
                <div className="flex max-h-[500px] w-72 flex-none flex-col gap-0.5 overflow-y-auto border-r bg-accent p-2">
                    {renderCategoryButton({
                        key: "all",
                        label: "All fields",
                        badge: ALL_FIELDS_BADGE,
                    })}

                    {sidebarGroups.map((group) => (
                        <div
                            key={group.label}
                            className="flex flex-col gap-0.5"
                        >
                            <div className="px-2 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                                {group.label}
                            </div>
                            {group.categories.map((cat) =>
                                renderCategoryButton(cat),
                            )}
                        </div>
                    ))}
                </div>

                <div
                    ref={listRef}
                    className="h-[500px] min-w-0 flex-1 overflow-y-auto p-1.5"
                >
                    {fieldRows}

                    {lookupResults.length === 0 && (
                        <div className="px-3.5 py-8 text-center text-sm leading-relaxed text-tertiary-foreground">
                            {isEmptySearch ? (
                                <>
                                    <span className="block">
                                        No fields match{" "}
                                        <span className="font-semibold text-secondary-foreground">
                                            “{lookupString.trim()}”
                                        </span>
                                        {category !== "all" &&
                                            activeCategoryLabel &&
                                            ` in ${activeCategoryLabel}`}
                                        .
                                    </span>
                                    <button
                                        type="button"
                                        className="text-link transition-colors hover:text-link-hover"
                                        onClick={() => {
                                            if (matchesOutsideCategory > 0) {
                                                // Keep the search, drop the
                                                // category that's hiding it
                                                setCategory("all");
                                            } else {
                                                setLookupString("");
                                            }
                                            setRawActiveIndex(0);
                                        }}
                                    >
                                        {matchesOutsideCategory > 0
                                            ? `Search all fields (${matchesOutsideCategory})`
                                            : "Clear search"}
                                    </button>
                                </>
                            ) : (
                                <span className="block">
                                    No fields available.
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
